import { writable } from 'svelte/store';
import { getDb } from './db';
import { syncMetaRepo } from './repo';
import type { HistoryEntry, NotificationSettings, Plan, PlanSession, Preferences, Task } from '$lib/domain/types';
import { normalizePreferences } from '$lib/domain/preferences';
import { normalizeNotificationSettings } from '$lib/domain/notifications';

export const SYNC_TABLES = ['tasks', 'plans', 'plan_sessions', 'history', 'preferences', 'notification_settings'] as const;
export type SyncTable = (typeof SYNC_TABLES)[number];

type SyncRecord = Record<string, unknown>;
type PreferencesRecord = { key: string; value: Preferences };
type NotificationSettingsRecord = { key: string; value: NotificationSettings };
type NormalizedRecord = Task | Plan | PlanSession | HistoryEntry | PreferencesRecord | NotificationSettingsRecord;
type SyncDeleteRecord = { id: string; deleted: true; deletedAt: string };

interface SyncChange {
  table: string;
  records: SyncRecord[];
}

interface PendingSyncEvent {
  table: string;
  recordId: string;
  payload: unknown;
}

type PullAppliedListener = (tables: readonly SyncTable[]) => void | Promise<void>;

export interface SyncStatus {
  state: 'idle' | 'syncing' | 'offline' | 'error' | 'disabled';
  pending: number;
  lastSyncAt?: string;
  error?: string;
  retryBlocked?: boolean;
}

class SyncOwnershipConflict extends Error {
  constructor() {
    super('Sync is paused because a record belongs to another sync identity. Your local changes are kept. Export a backup in Settings and check the original browser’s sync setup before retrying.');
    this.name = 'SyncOwnershipConflict';
  }
}

const isSyncTable = (value: string): value is SyncTable => SYNC_TABLES.includes(value as SyncTable);
const CLIENT_ID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const createTombstonePayload = (recordId: string, deletedAt = new Date().toISOString()): SyncDeleteRecord => ({
  id: recordId,
  deleted: true,
  deletedAt
});

export const isPulledTombstone = (record: SyncRecord): record is SyncDeleteRecord =>
  record.deleted === true && typeof record.id === 'string' && record.id.length > 0;

const errorName = (error: unknown) =>
  error && typeof error === 'object' && 'name' in error && typeof error.name === 'string' ? error.name : '';

const errorMessage = (error: unknown) =>
  error && typeof error === 'object' && 'message' in error && typeof error.message === 'string' ? error.message : '';

export const shouldLogSyncError = (error: unknown) =>
  !(errorName(error) === 'TypeError' && errorMessage(error) === 'Failed to fetch');

export const getOrCreateSyncClientId = () => {
  const key = 'ff_client_id';
  const existing = localStorage.getItem(key);
  if (existing && CLIENT_ID_PATTERN.test(existing)) return existing;
  const created = crypto.randomUUID();
  localStorage.setItem(key, created);
  return created;
};

const text = (record: SyncRecord, key: string, fallback = '') => {
  const value = record[key];
  return typeof value === 'string' ? value : fallback;
};

const optionalText = (record: SyncRecord, key: string) => {
  const value = record[key];
  return typeof value === 'string' && value.length > 0 ? value : undefined;
};

const numberValue = (record: SyncRecord, key: string, fallback = 0) => {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
};

const optionalNumber = (record: SyncRecord, key: string) => {
  const value = record[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
};

const booleanValue = (record: SyncRecord, key: string) => {
  const value = record[key];
  return value === true || value === 1;
};

const objectValue = <T>(record: SyncRecord, key: string, fallback: T): T => {
  const value = record[key];
  return value && typeof value === 'object' ? (value as T) : fallback;
};

export function normalizePulledRecord(table: 'tasks', record: SyncRecord): Task;
export function normalizePulledRecord(table: 'plans', record: SyncRecord): Plan;
export function normalizePulledRecord(table: 'plan_sessions', record: SyncRecord): PlanSession;
export function normalizePulledRecord(table: 'history', record: SyncRecord): HistoryEntry;
export function normalizePulledRecord(table: 'preferences', record: SyncRecord): PreferencesRecord;
export function normalizePulledRecord(table: 'notification_settings', record: SyncRecord): NotificationSettingsRecord;
export function normalizePulledRecord(table: SyncTable, record: SyncRecord): NormalizedRecord {
  switch (table) {
    case 'tasks':
      return {
        id: text(record, 'id'),
        title: text(record, 'title', 'Untitled task'),
        status: text(record, 'status') === 'done' ? 'done' : 'open',
        project: optionalText(record, 'project'),
        sortOrder: optionalNumber(record, 'sortOrder'),
        createdAt: text(record, 'createdAt', new Date().toISOString()),
        updatedAt: text(record, 'updatedAt', new Date().toISOString())
      } satisfies Task;
    case 'plans':
      return {
        id: text(record, 'id'),
        title: text(record, 'title', 'Focus plan'),
        startAt: text(record, 'startAt', new Date().toISOString()),
        autoStart: booleanValue(record, 'autoStart'),
        status: ['scheduled', 'active', 'completed', 'skipped'].includes(text(record, 'status'))
          ? (text(record, 'status') as Plan['status'])
          : 'scheduled',
        configSnapshot: objectValue<Plan['configSnapshot']>(record, 'configSnapshot', {
          workDuration: 25,
          shortBreakDuration: 5,
          longBreakDuration: 15,
          sessionsBeforeLongBreak: 4,
          totalSessions: 6
        }),
        sortOrder: optionalNumber(record, 'sortOrder'),
        createdAt: text(record, 'createdAt', new Date().toISOString()),
        updatedAt: text(record, 'updatedAt', new Date().toISOString())
      } satisfies Plan;
    case 'plan_sessions':
      return {
        id: text(record, 'id'),
        planId: text(record, 'planId'),
        index: numberValue(record, 'index'),
        type: ['work', 'short_break', 'long_break'].includes(text(record, 'type'))
          ? (text(record, 'type') as PlanSession['type'])
          : 'work',
        duration: numberValue(record, 'duration', 25),
        startedAt: optionalText(record, 'startedAt') ?? null,
        endedAt: optionalText(record, 'endedAt') ?? null,
        completed: booleanValue(record, 'completed')
      } satisfies PlanSession;
    case 'history':
      return {
        id: text(record, 'id'),
        sessionId: text(record, 'sessionId'),
        planId: text(record, 'planId'),
        sessionType: ['work', 'short_break', 'long_break'].includes(text(record, 'sessionType'))
          ? (text(record, 'sessionType') as PlanSession['type'])
          : undefined,
        duration: numberValue(record, 'duration'),
        taskId: optionalText(record, 'taskId'),
        rating: optionalNumber(record, 'rating'),
        mood: optionalText(record, 'mood') as HistoryEntry['mood'],
        notes: optionalText(record, 'notes'),
        interruptions: numberValue(record, 'interruptions'),
        pausedSeconds: numberValue(record, 'pausedSeconds'),
        createdAt: text(record, 'createdAt', new Date().toISOString())
      } satisfies HistoryEntry;
    case 'preferences':
      return {
        key: 'default',
        value: normalizePreferences(objectValue<Preferences>(record, 'value', { theme: 'liquid-light' }))
      };
    case 'notification_settings':
      return {
        key: 'default',
        value: normalizeNotificationSettings(record.value)
      };
  }
};

export const enqueueChange = async (table: string, recordId: string, payload: unknown) => {
  if (!isSyncTable(table)) return;
  const db = await getDb();
  await db.put('sync_events', {
    id: crypto.randomUUID(),
    table,
    recordId,
    payload,
    updatedAt: new Date().toISOString()
  });
};

export const enqueueDelete = async (table: string, recordId: string, deletedAt = new Date().toISOString()) => {
  await enqueueChange(table, recordId, createTombstonePayload(recordId, deletedAt));
};

export const createSyncEngine = (apiBaseUrl: string) => {
  const status = writable<SyncStatus>({ state: apiBaseUrl ? 'idle' : 'disabled', pending: 0 });
  let syncTimeout: number | null = null;
  let syncInFlight: Promise<void> | null = null;
  let retryBlocked = false;
  let backoff = 2000;
  const pullAppliedListeners = new Set<PullAppliedListener>();

  const onPullApplied = (listener: PullAppliedListener) => {
    pullAppliedListeners.add(listener);
    return () => pullAppliedListeners.delete(listener);
  };

  const updatePending = async (state?: SyncStatus['state'], error?: string) => {
    const db = await getDb();
    const pending = await db.count('sync_events');
    status.update((current) => ({
      ...current,
      pending,
      state: retryBlocked ? 'error' : state ?? current.state,
      error: error ?? (retryBlocked ? current.error : undefined),
      retryBlocked
    }));
  };

  const schedule = () => {
    if (retryBlocked || syncTimeout !== null) return;
    syncTimeout = window.setTimeout(async () => {
      syncTimeout = null;
      await sync();
    }, backoff);
  };

  const resetBackoff = () => {
    backoff = 2000;
  };

  const bumpBackoff = () => {
    backoff = Math.min(backoff * 2, 60000);
  };

  const runSync = async () => {
    if (!apiBaseUrl) {
      status.set({ state: 'disabled', pending: 0 });
      return;
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      await updatePending('offline');
      return;
    }

    try {
      await updatePending('syncing');
      await pushChanges();
      await pullChanges();
      resetBackoff();
      const lastSyncAt = new Date().toISOString();
      const db = await getDb();
      const pending = await db.count('sync_events');
      status.set({ state: 'idle', pending, lastSyncAt, retryBlocked: false });
      if (pending > 0) schedule();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sync failed';
      if (error instanceof SyncOwnershipConflict) {
        retryBlocked = true;
        if (syncTimeout !== null) {
          window.clearTimeout(syncTimeout);
          syncTimeout = null;
        }
        await updatePending('error', message);
        return;
      }
      if (shouldLogSyncError(error)) console.warn('[sync] failed, retrying', error);
      bumpBackoff();
      await updatePending('error', message);
      schedule();
    }
  };

  const sync = (): Promise<void> => {
    if (syncInFlight) return syncInFlight;
    if (retryBlocked) return Promise.resolve();
    if (syncTimeout !== null) {
      window.clearTimeout(syncTimeout);
      syncTimeout = null;
    }
    syncInFlight = runSync().finally(() => {
      syncInFlight = null;
    });
    return syncInFlight;
  };

  const retry = async () => {
    if (syncInFlight) await syncInFlight;
    retryBlocked = false;
    status.update((current) => ({ ...current, retryBlocked: false, error: undefined }));
    await sync();
  };

  const pushChanges = async () => {
    const db = await getDb();
    const events = await db.getAll('sync_events');
    if (!events.length) return;

    const clientId = getOrCreateSyncClientId();
    const meta = await syncMetaRepo.get(clientId);

    const response = await fetch(`${apiBaseUrl}/v1/sync/push`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ clientId, syncToken: meta.syncToken, events })
    });

    if (!response.ok) {
      if (response.status === 409) {
        const errorPayload: unknown = await response.json().catch(() => null);
        if (
          errorPayload && typeof errorPayload === 'object' &&
          'code' in errorPayload && errorPayload.code === 'record_owner_conflict' &&
          'retryable' in errorPayload && errorPayload.retryable === false
        ) {
          throw new SyncOwnershipConflict();
        }
      }
      throw new Error('Push failed');
    }
    const payload = (await response.json()) as { syncToken?: string };

    const tx = db.transaction('sync_events', 'readwrite');
    await Promise.all(events.map((event) => tx.store.delete(event.id)));
    await tx.done;

    await syncMetaRepo.set({ ...meta, syncToken: payload.syncToken ?? meta.syncToken, lastSyncAt: new Date().toISOString() });
  };

  const pullChanges = async () => {
    const clientId = getOrCreateSyncClientId();
    const meta = await syncMetaRepo.get(clientId);

    const response = await fetch(`${apiBaseUrl}/v1/sync/pull`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ clientId, syncToken: meta.syncToken })
    });

    if (!response.ok) throw new Error('Pull failed');
    const payload = (await response.json()) as { changes?: SyncChange[]; syncToken?: string };

    const db = await getDb();
    // Inspect the queue in the same transaction as applying remote records. A local
    // edit or deletion waiting for the next push wins over an older pull snapshot.
    const tx = db.transaction([...SYNC_TABLES, 'sync_events'], 'readwrite');
    const pendingEvents: PendingSyncEvent[] = await tx.objectStore('sync_events').getAll();
    const pendingByTable = new Map<SyncTable, Set<string>>();
    for (const event of pendingEvents) {
      if (!isSyncTable(event.table)) continue;
      const ids = pendingByTable.get(event.table) ?? new Set<string>();
      const records = Array.isArray(event.payload) ? event.payload : [event.payload];
      for (const record of records) {
        if (record && typeof record === 'object') {
          const value = record as SyncRecord;
          const id = event.table === 'preferences' || event.table === 'notification_settings'
            ? text(value, 'key', 'default')
            : text(value, 'id', event.recordId);
          ids.add(id);
        } else {
          ids.add(event.recordId);
        }
      }
      pendingByTable.set(event.table, ids);
    }
    const appliedTables = new Set<SyncTable>();
    for (const change of payload.changes ?? []) {
      if (!isSyncTable(change.table)) continue;
      for (const record of change.records) {
        const id = change.table === 'preferences' || change.table === 'notification_settings'
          ? text(record, 'key', 'default')
          : text(record, 'id');
        if (pendingByTable.get(change.table)?.has(id)) continue;
        appliedTables.add(change.table);
        if (isPulledTombstone(record)) {
          await tx.objectStore(change.table).delete(record.id);
          continue;
        }

        switch (change.table) {
          case 'tasks':
            await tx.objectStore('tasks').put(normalizePulledRecord('tasks', record));
            break;
          case 'plans':
            await tx.objectStore('plans').put(normalizePulledRecord('plans', record));
            break;
          case 'plan_sessions':
            await tx.objectStore('plan_sessions').put(normalizePulledRecord('plan_sessions', record));
            break;
          case 'history':
            await tx.objectStore('history').put(normalizePulledRecord('history', record));
            break;
          case 'preferences':
            await tx.objectStore('preferences').put(normalizePulledRecord('preferences', record));
            break;
          case 'notification_settings':
            await tx.objectStore('notification_settings').put(normalizePulledRecord('notification_settings', record));
            break;
        }
      }
    }
    await tx.done;

    await syncMetaRepo.set({ ...meta, syncToken: payload.syncToken ?? meta.syncToken, lastSyncAt: new Date().toISOString() });
    if (appliedTables.size) {
      const tables = [...appliedTables];
      await Promise.all([...pullAppliedListeners].map((listener) => listener(tables)));
    }
  };

  const enqueue = async (table: string, recordId: string, payload: unknown) => {
    await enqueueChange(table, recordId, payload);
    await updatePending();
    schedule();
  };

  return { subscribe: status.subscribe, sync, retry, schedule, enqueueChange: enqueue, onPullApplied };
};
